import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { ContactInput, Contact } from '../types';
import { apiService, buildApiUrl } from '../services/api';
import { validatePhone, normalizePhoneInput } from '../utils/phoneUtils';
import { maskCPF } from '../utils/documentUtils';

interface Category {
  id: string;
  nome: string;
}

const contactSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  document: z
    .string()
    .optional()
    .refine(
      (value) => !value || value.replace(/\D/g, '').length === 11,
      'CPF deve ter 11 dígitos'
    ),
  telefone: z.string().min(1, 'Telefone é obrigatório').refine(validatePhone, {
    message: 'Formato de telefone inválido',
  }),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  endereco: z.string().optional(),
  processo: z.string().optional(),
  mandado: z.string().optional(),
  observacoes: z.string().optional(),
  tags: z.string().optional(),
  categoriaId: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

interface ContactFormProps {
  contact?: Contact;
  onSuccess: () => void | Promise<void>;
  onCancel: () => void;
}

export function ContactForm({ contact, onSuccess, onCancel }: ContactFormProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      nome: contact?.nome || '',
      telefone: contact?.telefone || '+55 ',
      email: contact?.email || '',
      document: contact?.document || '',
      endereco: contact?.endereco || '',
      processo: contact?.processo || '',
      mandado: contact?.mandado || '',
      observacoes: contact?.observacoes || '',
      tags: contact?.tags?.join(', ') || '',
      categoriaId: contact?.categoriaId || '',
    },
  });

  const documentValue = watch('document');
  const phoneValue = watch('telefone');

  useEffect(() => {
    const masked = maskCPF(documentValue || '');
    if (masked !== (documentValue || '')) {
      setValue('document', masked);
    }
  }, [documentValue, setValue]);

  useEffect(() => {
    const normalized = normalizePhoneInput(phoneValue || '');
    if (normalized !== (phoneValue || '')) {
      setValue('telefone', normalized);
    }
  }, [phoneValue, setValue]);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(buildApiUrl('/categorias'), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const onSubmit = async (data: ContactFormData) => {
    try {
      const cleanDocument = (data.document || '').replace(/\D/g, '');
      const normalizedPhone = (data.telefone || '').trim();
      const normalizedTags = (data.tags || '')
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);

      const contactInput: ContactInput = {
        nome: data.nome,
        telefone: normalizedPhone,
        document: cleanDocument || undefined,
        email: data.email || undefined,
        endereco: data.endereco?.trim() || undefined,
        processo: data.processo?.trim() || undefined,
        mandado: data.mandado?.trim() || undefined,
        observacoes: data.observacoes || undefined,
        tags: normalizedTags,
        categoriaId: data.categoriaId || undefined,
      };

      if (contact) {
        await apiService.updateContact(contact.id, contactInput);
        toast.success('Destinatário atualizado com sucesso');
      } else {
        await apiService.createContact(contactInput);
        toast.success('Destinatário criado com sucesso');
      }

      await onSuccess();
    } catch (err: any) {
      if (err?.isQuotaError || err?.upgradeRequired) {
        toast.error(err.message || 'Limite de destinatários atingido. Faça upgrade do plano para continuar.', {
          duration: 6000,
          icon: '!',
        });
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'Erro ao salvar destinatário';
      toast.error(errorMessage);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black bg-opacity-50 p-4 backdrop-blur-sm sm:items-center sm:p-6">
      <div
        className="max-h-[calc(100vh-2rem)] w-full max-w-2xl overflow-y-auto rounded-2xl border border-gray-100 bg-white p-6 shadow-2xl sm:p-8"
        role="dialog"
        aria-labelledby="form-title"
      >
        <div className="mb-6 text-center sm:mb-8">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 sm:h-16 sm:w-16">
            <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 id="form-title" className="text-xl font-bold text-gray-900 sm:text-2xl">
            {contact ? 'Editar Destinatário' : 'Novo Destinatário'}
          </h2>
          <p className="mt-2 text-gray-500">
            {contact ? 'Atualize os dados do destinatário' : 'Preencha os dados para cadastrar um destinatário'}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 sm:space-y-6">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="nome" className="mb-2 block text-sm font-semibold text-gray-700">
                Nome *
              </label>
              <input id="nome" type="text" {...register('nome')} className="input-field" placeholder="Digite o nome completo" />
              {errors.nome && <p className="mt-1 text-sm text-red-500">{errors.nome.message}</p>}
            </div>

            <div>
              <label htmlFor="telefone" className="mb-2 block text-sm font-semibold text-gray-700">
                Telefone *
              </label>
              <input id="telefone" type="tel" {...register('telefone')} placeholder="+55 11 99999-9999" className="input-field" />
              {errors.telefone && <p className="mt-1 text-sm text-red-500">{errors.telefone.message}</p>}
            </div>

            <div>
              <label htmlFor="document" className="mb-2 block text-sm font-semibold text-gray-700">
                CPF
              </label>
              <input id="document" type="text" {...register('document')} className="input-field" placeholder="Digite o número do CPF" />
              {errors.document && <p className="mt-1 text-sm text-red-500">{errors.document.message}</p>}
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="email" className="mb-2 block text-sm font-semibold text-gray-700">
                Email
              </label>
              <input id="email" type="email" {...register('email')} className="input-field" placeholder="Digite o email" />
              {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>}
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="endereco" className="mb-2 block text-sm font-semibold text-gray-700">
                Endereço
              </label>
              <input id="endereco" type="text" {...register('endereco')} className="input-field" placeholder="Digite o endereço do destinatário" />
            </div>

            <div>
              <label htmlFor="processo" className="mb-2 block text-sm font-semibold text-gray-700">
                Processo
              </label>
              <input id="processo" type="text" {...register('processo')} className="input-field" placeholder="Número do processo" />
            </div>

            <div>
              <label htmlFor="mandado" className="mb-2 block text-sm font-semibold text-gray-700">
                Mandado
              </label>
              <input id="mandado" type="text" {...register('mandado')} className="input-field" placeholder="Tipo ou identificação do mandado" />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="categoriaId" className="mb-2 block text-sm font-semibold text-gray-700">
                Categoria do destinatário
              </label>
              <select id="categoriaId" {...register('categoriaId')} className="input-field" disabled={loadingCategories}>
                <option value="">Selecione uma categoria</option>
                {loadingCategories ? (
                  <option disabled>Carregando categorias...</option>
                ) : (
                  categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.nome}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="tags" className="mb-2 block text-sm font-semibold text-gray-700">
                Marcadores
              </label>
              <input id="tags" type="text" {...register('tags')} className="input-field" placeholder="Ex: urgente, mandado, prioridade" />
              <p className="mt-1 text-xs text-gray-500">
                Separe os marcadores por vírgula para facilitar filtros e segmentações.
              </p>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="observacoes" className="mb-2 block text-sm font-semibold text-gray-700">
                Observações
              </label>
              <textarea id="observacoes" {...register('observacoes')} rows={4} className="input-field resize-none" placeholder="Informações complementares" />
            </div>
          </div>

          <div className="flex flex-col-reverse gap-3 pt-4 sm:flex-row sm:pt-6">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 rounded-xl border border-gray-200 bg-gray-100 px-6 py-3 font-medium text-gray-700 transition-all duration-200 hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary flex flex-1 items-center justify-center gap-2 px-6 py-3 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? 'Salvando...' : 'Salvar destinatário'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
